// src/components/ShareModal.tsx
import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Switch,
  DatePicker,
  Button,
  message,
  Space,
  Radio,
  Divider
} from 'antd';
import {
  ShareAltOutlined,
  LockOutlined,
  MailOutlined,
  CopyOutlined,
  CalendarOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  VideoCameraOutlined,
  TeamOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useCustomMutation } from '@refinedev/core';
import type { CreateShareRequest, CreateShareResponse, ShareType } from '../types/share';
import dayjs from 'dayjs';
import { auth } from '../utils/firebase';

const { TextArea } = Input;

interface ShareModalProps {
  visible: boolean;
  onClose: () => void;
  jobId: string;
  videoTitle: string;
  onShareCreated?: (shareUrl: string, shareToken: string) => void;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  visible,
  onClose,
  jobId,
  videoTitle,
  onShareCreated
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [shareToken, setShareToken] = useState<string>('');
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [expirationEnabled, setExpirationEnabled] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(true);
  const [emailsValue, setEmailsValue] = useState('');
  const [sendMode, setSendMode] = useState<'individual' | 'cc'>('individual');
  const [shareType, setShareType] = useState<ShareType>('collaboration');

  const { mutate: createShare } = useCustomMutation();

  const handleCreateShare = async (values: any) => {
    setLoading(true);

    try {
      // Parse email recipients
      const recipients = emailsValue
        .split(/[,;\n]/)
        .map(email => email.trim())
        .filter(email => email.length > 0);

      // Validate emails
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = recipients.filter(email => !emailRegex.test(email));

      if (invalidEmails.length > 0) {
        message.error(`Invalid email addresses: ${invalidEmails.join(', ')}`);
        setLoading(false);
        return;
      }

      const requestData: CreateShareRequest = {
        jobId,
        trackingEnabled,
        recipients: recipients,
        password: passwordEnabled ? values.password : undefined,
        expiresAt: expirationEnabled && values.expiresAt
          ? values.expiresAt.toDate()
          : undefined,
        customMessage: values.customMessage || undefined,
        shareType,
      };

      createShare({
        url: '/create-share',
        method: 'post',
        values: requestData,
      }, {
        onSuccess: (data: any) => {
          console.log('=== CREATE SHARE RESPONSE ===');
          console.log('Full data object:', data);
          console.log('data.data:', data.data);
          console.log('============================');

          // Extract response - handle multiple possible structures:
          // 1. data.data.data (dataProvider wraps backend's {data: ...})
          // 2. data.data (direct backend response)
          // 3. data (already unwrapped)
          let response: CreateShareResponse;

          if (data.data?.data?.shareToken) {
            // Structure: { data: { data: { shareToken, shareUrl } } }
            response = data.data.data as CreateShareResponse;
          } else if (data.data?.shareToken) {
            // Structure: { data: { shareToken, shareUrl } }
            response = data.data as CreateShareResponse;
          } else if (data.shareToken) {
            // Structure: { shareToken, shareUrl }
            response = data as CreateShareResponse;
          } else {
            console.error('Unable to extract shareToken from response');
            console.error('Response structure:', JSON.stringify(data, null, 2));
            message.error('Failed to create share: Invalid response format');
            setLoading(false);
            return;
          }

          console.log('=== EXTRACTED RESPONSE ===');
          console.log('response:', response);
          console.log('shareToken:', response.shareToken);
          console.log('shareUrl:', response.shareUrl);
          console.log('==========================');

          if (!response.shareToken || !response.shareUrl) {
            console.error('Missing shareToken or shareUrl in response');
            message.error('Failed to create share: Missing required fields');
            setLoading(false);
            return;
          }

          setShareUrl(response.shareUrl);
          setShareToken(response.shareToken);

          // If emails provided, send them (silent - no separate notification)
          if (recipients.length > 0) {
            handleSendEmails(response.shareToken, recipients, values.customMessage, true);
          }

          // Copy to clipboard and show consolidated success message
          navigator.clipboard.writeText(response.shareUrl)
            .then(() => {
              // Build success message with clipboard success
              const successParts = ['Share link created', 'copied to clipboard'];
              if (recipients.length > 0) successParts.push(`email sent to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}`);
              message.success(successParts.join(', ') + '!');
            })
            .catch(() => {
              // Build success message without clipboard
              const successParts = ['Share link created'];
              if (recipients.length > 0) successParts.push(`email sent to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}`);
              message.success(successParts.join(', ') + '!');
            });

          if (onShareCreated) {
            onShareCreated(response.shareUrl, response.shareToken);
          }
        },
        onError: (error: any) => {
          console.error('Error creating share:', error);
          message.error(error?.message || 'Failed to create share link');
          setLoading(false);
        },
      });

      setLoading(false);
    } catch (error) {
      console.error('Error creating share:', error);
      message.error('Failed to create share link');
      setLoading(false);
    }
  };

  const handleSendEmails = async (token: string, recipients: string[], customMessage?: string, silent: boolean = false) => {
    try {
      // Use direct fetch to avoid dataProvider field filtering issues
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const authToken = await auth.currentUser?.getIdToken();

      if (!authToken) {
        if (!silent) message.error('Authentication required');
        return;
      }

      const requestBody = {
        share_token: token,  // Send as snake_case
        recipients,
        custom_message: customMessage || '',
        send_mode: sendMode,
      };

      console.log('=== SENDING EMAIL REQUEST ===');
      console.log('Token received:', token);
      console.log('Request body:', requestBody);
      console.log('============================');

      const response = await fetch(`${API_URL}/send-share-emails`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send emails');
      }

      // Only show notification if not in silent mode
      if (!silent) {
        message.success(`Emails sent to ${recipients.length} recipient(s)`);
      }
    } catch (error: any) {
      console.error('Error sending emails:', error);
      // Always show errors
      message.warning('Share created but failed to send emails');
    }
  };

  const handleCopyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        message.success('Share link copied to clipboard!');
      });
    }
  };

  const handleClose = () => {
    form.resetFields();
    setShareUrl('');
    setShareToken('');
    setPasswordEnabled(false);
    setExpirationEnabled(false);
    setTrackingEnabled(true);
    setEmailsValue('');
    setSendMode('individual');
    setShareType('collaboration');
    onClose();
  };

  const defaultMessage = `I wanted to share this inspection video with you: "${videoTitle}". Click the link below to view.`;

  return (
    <Modal
      title={
        <Space>
          <ShareAltOutlined />
          <span>Share Review</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={600}
      footer={
        shareUrl ? (
          <Space>
            <Button onClick={handleClose}>Close</Button>
            <Button type="primary" icon={<CopyOutlined />} onClick={handleCopyLink}>
              Copy Link
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={loading}
            >
              Share Now
            </Button>
          </Space>
        )
      }
    >
      {shareUrl ? (
        // Success view - show share URL
        <div>
          <div style={{
            padding: '16px',
            backgroundColor: '#f0f9ff',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <h4 style={{ marginBottom: '8px', color: '#1890ff' }}>
              Share link created successfully!
            </h4>
            <p style={{ marginBottom: '12px', color: '#666' }}>
              Your video is ready to share. Copy the link below:
            </p>
            <Input
              value={shareUrl}
              readOnly
              suffix={
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={handleCopyLink}
                  size="small"
                />
              }
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
          </div>

          <div style={{ fontSize: '13px', color: '#666' }}>
            <p><strong>Note:</strong></p>
            <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
              <li>Anyone with this link can view the video</li>
              {shareType === 'collaboration' && (
                <li style={{ color: '#52c41a' }}>
                  <strong>Collaboration enabled:</strong> Recipients can view and comment on the PDF report
                </li>
              )}
              {passwordEnabled && <li>Password protection is enabled</li>}
              {expirationEnabled && <li>Link will expire on the specified date</li>}
              {trackingEnabled && <li>View analytics are enabled</li>}
            </ul>
          </div>
        </div>
      ) : (
        // Form view - create share
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateShare}
          initialValues={{
            customMessage: defaultMessage,
            trackingEnabled: true,
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ marginBottom: '4px' }}>Sharing: {videoTitle}</h4>
            <p style={{ color: '#666', fontSize: '13px', margin: 0 }}>
              Create a shareable link for this inspection video
            </p>
          </div>

          {/* Share Type Selection - Prominent at top */}
          <div style={{
            backgroundColor: '#f6f8fa',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <div style={{ marginBottom: '12px', fontWeight: 600 }}>
              What would you like to share?
            </div>
            <Radio.Group
              value={shareType}
              onChange={(e) => setShareType(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Radio value="collaboration" style={{ width: '100%' }}>
                  <Space>
                    <TeamOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                    <div>
                      <div style={{ fontWeight: 500 }}>Collaborate on Inspection Report</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Video and full PDF inspection report with comment sharing
                      </div>
                    </div>
                  </Space>
                </Radio>
                <Radio value="video" style={{ width: '100%' }}>
                  <Space>
                    <VideoCameraOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                    <div>
                      <div style={{ fontWeight: 500 }}>Video Review Only</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Share the video summary of the inspection
                      </div>
                    </div>
                  </Space>
                </Radio>
              </Space>
            </Radio.Group>
            {shareType === 'collaboration' && (
              <div style={{
                marginTop: '12px',
                padding: '8px 12px',
                backgroundColor: '#e6f7ff',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#1890ff',
              }}>
                <FileTextOutlined style={{ marginRight: '6px' }} />
                Recipients will be able to view the PDF report and add comments directly on the document.
              </div>
            )}
          </div>

          <Divider />

          {/* Email Recipients */}
          <Form.Item
            label={
              <Space>
                <MailOutlined />
                <span>Email Recipients (optional)</span>
              </Space>
            }
            help="Enter email addresses separated by commas, semicolons, or new lines"
          >
            <TextArea
              value={emailsValue}
              onChange={(e) => setEmailsValue(e.target.value)}
              placeholder="john@example.com, jane@example.com"
              rows={2}
            />
          </Form.Item>

          {emailsValue && (
            <Form.Item label="Send Mode">
              <Radio.Group
                value={sendMode}
                onChange={(e) => setSendMode(e.target.value)}
              >
                <Radio value="individual">Send individual emails</Radio>
                <Radio value="cc">Send one email with all recipients CC'd</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          {/* Custom Message */}
          <Form.Item
            label="Custom Message"
            name="customMessage"
          >
            <TextArea
              rows={3}
              placeholder="Add a personal message (optional)"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Divider />

          {/* Password Protection */}
          <Form.Item label="Password Protection">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Switch
                checked={passwordEnabled}
                onChange={setPasswordEnabled}
                checkedChildren={<LockOutlined />}
                unCheckedChildren={<LockOutlined />}
              />
              {passwordEnabled && (
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: 'Please enter a password' },
                    { min: 8, message: 'Password must be at least 8 characters' },
                    {
                      pattern: /^(?=.*[A-Za-z])(?=.*\d)/,
                      message: 'Password must contain at least one letter and one number'
                    }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input.Password
                    placeholder="Enter password (min 8 characters, 1 letter, 1 number)"
                    prefix={<LockOutlined />}
                  />
                </Form.Item>
              )}
            </Space>
          </Form.Item>

          {/* Expiration Date */}
          <Form.Item label="Expiration Date">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Switch
                checked={expirationEnabled}
                onChange={setExpirationEnabled}
                checkedChildren={<CalendarOutlined />}
                unCheckedChildren={<CalendarOutlined />}
              />
              {expirationEnabled && (
                <Form.Item
                  name="expiresAt"
                  rules={[
                    { required: true, message: 'Please select an expiration date' },
                    {
                      validator: (_, value) => {
                        if (!value || value.isAfter(dayjs())) {
                          return Promise.resolve();
                        }
                        return Promise.reject('Expiration date must be in the future');
                      }
                    }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                  />
                </Form.Item>
              )}
            </Space>
          </Form.Item>

          {/* Tracking */}
          <Form.Item
            label="Enable View Analytics"
            name="trackingEnabled"
            valuePropName="checked"
          >
            <Switch
              checked={trackingEnabled}
              onChange={setTrackingEnabled}
              checkedChildren={<EyeOutlined />}
              unCheckedChildren={<EyeInvisibleOutlined />}
            />
          </Form.Item>

          <div style={{
            backgroundColor: '#f6f8fa',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#666',
            marginTop: '16px'
          }}>
            <strong>Privacy Note:</strong> Share links are publicly accessible by anyone with the URL.
            Use password protection for sensitive content.
          </div>
        </Form>
      )}
    </Modal>
  );
};
